// Clients list (SPEC §4): full v_client_list fetch with client-side search,
// filters, sortable table, bulk actions (tag / acquisition source / bulk SMS),
// mobile card layout, and CSV export.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { sendBulk } from "../lib/api";
import type { BulkSmsResult } from "../lib/api";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import Modal from "../components/Modal";
import TagChips from "../components/TagChips";
import {
  DueBadge,
  PackageBadge,
  RetentionDot,
  SEGMENT_LABELS,
  SegmentChip,
  SourceBadge,
  WhaleBadge,
} from "../components/Badges";
import { mailtoHref, money, phoneDisplay, telHref } from "../lib/format";
import { fmtDate } from "../lib/dates";
import { RETENTION_COLORS, RETENTION_EMOJI, RETENTION_LABELS, retentionColor } from "../lib/retention";
import type { AcquisitionSource, ClientRow, Id, Segment } from "../lib/types";

const SOURCE_OPTIONS: { value: AcquisitionSource; label: string }[] = [
  { value: "google_ads", label: "Google Ads" },
  { value: "seo_gbp", label: "SEO (GBP)" },
  { value: "referral", label: "Referral" },
  { value: "organic", label: "Organic" },
  { value: "other", label: "Other" },
];

/** PostgREST can serialize numerics as strings; normalize before math. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isNaN(n) ? null : n;
}

/** Today's business date (YYYY-MM-DD, America/New_York). */
function nyTodayYmd(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function csvCell(v: unknown): string {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

type PkgFilter = "any" | "yes" | "no";

export default function Clients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rfmParam = searchParams.get("rfm");
  const colorParam = searchParams.get("color");
  const segmentId = searchParams.get("segment");

  const {
    data,
    error: qError,
    loading,
    reload,
  } = useQuery<ClientRow[]>(async () => {
    const { data: rows, error } = await supabase
      .from("v_client_list")
      .select("*")
      .order("full_name", { ascending: true });
    if (error) throw new Error(error.message);
    return (rows ?? []) as ClientRow[];
  }, []);

  // ---- filters ----
  const [search, setSearch] = useState("");
  const [pkgFilter, setPkgFilter] = useState<PkgFilter>("any");
  const [dueOnly, setDueOnly] = useState(false);
  const [dueWeeks, setDueWeeks] = useState(6);
  const [ltvMin, setLtvMin] = useState("");
  const [ltvMax, setLtvMax] = useState("");
  const [recency, setRecency] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [segFilter, setSegFilter] = useState("");
  const [whaleOnly, setWhaleOnly] = useState(false);
  const [colorFilter, setColorFilter] = useState("");

  // ?rfm=<segment> preselects the RFM-segment filter (chips link here), then
  // is stripped from the URL so a later chip click re-applies it.
  useEffect(() => {
    if (rfmParam == null) return;
    setSegFilter(rfmParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("rfm");
        return next;
      },
      { replace: true },
    );
  }, [rfmParam, setSearchParams]);

  // ?color=<green|yellow|red> preselects the retention-color filter (the Home
  // board's "show all" links here), same strip-after-apply pattern as ?rfm=.
  useEffect(() => {
    if (colorParam == null) return;
    if ((RETENTION_COLORS as string[]).includes(colorParam)) setColorFilter(colorParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("color");
        return next;
      },
      { replace: true },
    );
  }, [colorParam, setSearchParams]);

  // ?segment=<uuid> — saved-segment view: fetch the segments row, resolve
  // members via the segment_client_ids RPC (the single segment evaluator),
  // and restrict the list to those ids. Cleared via the banner's X.
  const [segView, setSegView] = useState<{ segment: Segment; ids: Set<string> } | null>(null);
  const [segViewError, setSegViewError] = useState<string | null>(null);

  useEffect(() => {
    if (!segmentId) {
      setSegView(null);
      setSegViewError(null);
      return;
    }
    let cancelled = false;
    setSegView(null);
    setSegViewError(null);
    void (async () => {
      try {
        const { data: seg, error } = await supabase
          .from("segments")
          .select("*")
          .eq("id", segmentId)
          .single();
        if (error) throw new Error(error.message);
        const segment = seg as Segment;
        const { data: idRows, error: rpcErr } = await supabase.rpc("segment_client_ids", {
          p_def: segment.definition,
        });
        if (rpcErr) throw new Error(rpcErr.message);
        const ids = new Set(
          ((idRows ?? []) as { client_id: Id }[]).map((r) => String(r.client_id)),
        );
        if (!cancelled) setSegView({ segment, ids });
      } catch (e) {
        if (!cancelled) setSegViewError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [segmentId]);

  // ---- selection & bulk actions ----
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const [bulkSource, setBulkSource] = useState("");
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  // Who the compose modal sends to: the checkbox selection, or every member of
  // the viewed saved segment (server-side via {segment_id}).
  const [smsTarget, setSmsTarget] = useState<"selected" | "segment">("selected");
  const [smsBody, setSmsBody] = useState("");
  const [bulkResult, setBulkResult] = useState<BulkSmsResult | null>(null);
  // Chunk-loop progress while a bulk send runs ("Sending 80/220…").
  const [smsProgress, setSmsProgress] = useState<{ sent: number; total: number } | null>(null);

  const rows = data ?? [];
  const total = rows.length;

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) for (const t of r.tags ?? []) set.add(t);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, "");
    const min = ltvMin.trim() === "" ? null : Number(ltvMin);
    const max = ltvMax.trim() === "" ? null : Number(ltvMax);
    const rec = recency === "" ? null : Number(recency);
    const today = nyTodayYmd();

    return rows.filter((r) => {
      // Saved-segment view: only members (nothing while it is still resolving).
      if (segmentId && (!segView || !segView.ids.has(String(r.id)))) return false;
      if (q) {
        const hay = `${r.full_name ?? ""} ${r.email ?? ""} ${r.phone_e164 ?? ""}`.toLowerCase();
        const phoneDigits = (r.phone_e164 ?? "").replace(/\D/g, "");
        const hit = hay.includes(q) || (qDigits.length >= 3 && phoneDigits.includes(qDigits));
        if (!hit) return false;
      }
      if (pkgFilter === "yes" && !r.has_active_package) return false;
      if (pkgFilter === "no" && r.has_active_package) return false;

      const weeks = num(r.weeks_since_last_visit);
      if (dueOnly) {
        // Mirrors v_rebook_due semantics: overdue, contactable, not snoozed.
        if (weeks == null || weeks < dueWeeks) return false;
        if (r.sms_opt_out) return false;
        if (r.rebook_snoozed_until && r.rebook_snoozed_until >= today) return false;
      }

      const ltv = num(r.ltv) ?? 0;
      if (min != null && !Number.isNaN(min) && ltv < min) return false;
      if (max != null && !Number.isNaN(max) && ltv > max) return false;

      if (rec != null && (weeks == null || weeks > rec)) return false;

      if (tagFilter && !(r.tags ?? []).includes(tagFilter)) return false;

      if (sourceFilter === "none" && r.acquisition_source != null) return false;
      if (sourceFilter && sourceFilter !== "none" && r.acquisition_source !== sourceFilter) {
        return false;
      }

      if (segFilter && r.rfm_segment !== segFilter) return false;
      if (whaleOnly && !r.is_whale) return false;
      // Retention color: THE shared derivation (lib/retention.ts), computed
      // from v_client_list fields — never stored.
      if (colorFilter && retentionColor(r) !== colorFilter) return false;
      return true;
    });
  }, [
    rows,
    search,
    pkgFilter,
    dueOnly,
    dueWeeks,
    ltvMin,
    ltvMax,
    recency,
    tagFilter,
    sourceFilter,
    segFilter,
    whaleOnly,
    colorFilter,
    segmentId,
    segView,
  ]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(String(r.id))),
    [rows, selected],
  );

  const hasActiveFilters =
    search !== "" ||
    pkgFilter !== "any" ||
    dueOnly ||
    ltvMin !== "" ||
    ltvMax !== "" ||
    recency !== "" ||
    tagFilter !== "" ||
    sourceFilter !== "" ||
    segFilter !== "" ||
    colorFilter !== "" ||
    whaleOnly;

  function resetFilters() {
    setSearch("");
    setPkgFilter("any");
    setDueOnly(false);
    setDueWeeks(6);
    setLtvMin("");
    setLtvMax("");
    setRecency("");
    setTagFilter("");
    setSourceFilter("");
    setSegFilter("");
    setColorFilter("");
    setWhaleOnly(false);
  }

  function toggleSelect(id: Id) {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = String(id);
      if (next.has(k)) {
        next.delete(k);
      } else {
        next.add(k);
      }
      return next;
    });
  }

  function selectAllFiltered() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of filtered) next.add(String(r.id));
      return next;
    });
  }

  async function runAction(fn: () => Promise<void>) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function bulkAddTag() {
    const tag = tagDraft.trim().toLowerCase();
    if (!tag || selectedRows.length === 0) return;
    void runAction(async () => {
      const inserts = selectedRows.map((r) => ({ client_id: r.id, tag }));
      const { error } = await supabase
        .from("client_tags")
        .upsert(inserts, { onConflict: "client_id,tag", ignoreDuplicates: true });
      if (error) throw new Error(error.message);
      setTagModalOpen(false);
      setTagDraft("");
      reload();
    });
  }

  function bulkSetSource() {
    if (!bulkSource || selectedRows.length === 0) return;
    void runAction(async () => {
      const { error } = await supabase.rpc("set_acquisition", {
        p_client_ids: selectedRows.map((r) => r.id),
        p_source: bulkSource === "clear" ? null : bulkSource,
      });
      if (error) throw new Error(error.message);
      setBulkSource("");
      reload();
    });
  }

  function bulkSendText() {
    const body = smsBody.trim();
    if (!body) return;
    const args =
      smsTarget === "segment"
        ? segView
          ? { segment_id: segView.segment.id, body }
          : null
        : selectedRows.length > 0
          ? { client_ids: selectedRows.map((r) => r.id), body }
          : null;
    if (!args) return;
    setSmsProgress(null);
    void runAction(async () => {
      try {
        const result = await sendBulk(args, (sentSoFar, totalPlanned) =>
          setSmsProgress({ sent: sentSoFar, total: totalPlanned }),
        );
        setBulkResult(result);
      } finally {
        setSmsProgress(null);
      }
    });
  }

  function openSmsModal(target: "selected" | "segment") {
    setActionError(null);
    setBulkResult(null);
    setSmsBody("");
    setSmsTarget(target);
    setSmsModalOpen(true);
  }

  function exportCsv() {
    const header = [
      "name",
      "email",
      "phone",
      "segment",
      "whale",
      "avg_ticket",
      "ltv",
      "visits",
      "first_visit",
      "last_visit",
      "weeks_since_last_visit",
      "package_balance",
      "tags",
      "acquisition_source",
      "sms_opt_out",
    ];
    const lines = [header.join(",")];
    for (const r of filtered) {
      lines.push(
        [
          csvCell(r.full_name),
          csvCell(r.email),
          csvCell(r.phone_e164),
          csvCell(r.rfm_segment),
          csvCell(r.is_whale ? "yes" : "no"),
          csvCell(num(r.avg_ticket)),
          csvCell(num(r.ltv) ?? 0),
          csvCell(num(r.visits) ?? 0),
          csvCell(r.first_visit),
          csvCell(r.last_visit),
          csvCell(num(r.weeks_since_last_visit)),
          csvCell(num(r.active_package_balance)),
          csvCell((r.tags ?? []).join("; ")),
          csvCell(r.acquisition_source),
          csvCell(r.sms_opt_out ? "yes" : "no"),
        ].join(","),
      );
    }
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clients-${nyTodayYmd()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const columns: Column<ClientRow>[] = [
    {
      key: "_select",
      label: "",
      width: "34px",
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.has(String(r.id))}
          onChange={() => toggleSelect(r.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${r.full_name}`}
        />
      ),
      sortValue: (r) => (selected.has(String(r.id)) ? 0 : 1),
    },
    {
      key: "full_name",
      label: "Name",
      render: (r) => (
        <div>
          <RetentionDot color={retentionColor(r)} />{" "}
          <span className="bold">{r.full_name}</span>
          {r.sms_opt_out && (
            <span className="badge badge-red" style={{ marginLeft: 6 }}>
              Opt-out
            </span>
          )}
          <div>
            <PackageBadge balance={num(r.active_package_balance)} />
            <DueBadge weeks={num(r.weeks_since_last_visit)} threshold={dueWeeks} />
            <SourceBadge source={r.acquisition_source} />
          </div>
        </div>
      ),
      sortValue: (r) => r.full_name,
    },
    {
      key: "phone_e164",
      label: "Phone",
      render: (r) =>
        r.phone_e164 ? (
          <span className="nowrap">
            <a href={telHref(r.phone_e164)} onClick={(e) => e.stopPropagation()}>
              {phoneDisplay(r.phone_e164)}
            </a>{" "}
            <button
              type="button"
              className="btn-link"
              title={`Text ${r.full_name}`}
              aria-label={`Text ${r.full_name}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/clients/${r.id}?compose=1`);
              }}
            >
              💬
            </button>
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "email",
      label: "Email",
      render: (r) =>
        r.email ? (
          <a
            href={mailtoHref(r.email)}
            className="truncate"
            style={{ maxWidth: 180, display: "inline-block", verticalAlign: "bottom" }}
            onClick={(e) => e.stopPropagation()}
          >
            {r.email}
          </a>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "rfm_segment",
      label: "Segment",
      render: (r) => (
        <span className="nowrap">
          <SegmentChip segment={r.rfm_segment} link />
          <WhaleBadge whale={r.is_whale} />
        </span>
      ),
      sortValue: (r) => (r.rfm_segment ?? "") + (r.is_whale ? "!" : ""),
    },
    {
      key: "tags",
      label: "Tags",
      render: (r) => <TagChips tags={r.tags ?? []} />,
      sortValue: (r) => (r.tags ?? []).join(","),
    },
    {
      key: "avg_ticket",
      label: "Avg ticket",
      align: "right",
      render: (r) => {
        const v = num(r.avg_ticket);
        return v == null ? <span className="muted">—</span> : money(v);
      },
      sortValue: (r) => num(r.avg_ticket),
    },
    {
      key: "ltv",
      label: "LTV",
      align: "right",
      render: (r) => {
        const v = num(r.ltv) ?? 0;
        return <span className={v < 0 ? "red" : undefined}>{money(v)}</span>;
      },
      sortValue: (r) => num(r.ltv),
    },
    {
      key: "visits",
      label: "Visits",
      align: "right",
      render: (r) => num(r.visits) ?? 0,
      sortValue: (r) => num(r.visits),
    },
    {
      key: "last_visit",
      label: "Last visit",
      render: (r) =>
        r.last_visit ? (
          <span className="nowrap">
            {fmtDate(r.last_visit)}{" "}
            <span className="muted small">({num(r.weeks_since_last_visit)}w)</span>
          </span>
        ) : (
          <span className="muted">Never</span>
        ),
      sortValue: (r) => r.last_visit,
    },
    {
      key: "active_package_balance",
      label: "Pkg bal",
      align: "right",
      render: (r) => {
        const b = num(r.active_package_balance);
        return b == null ? <span className="muted">—</span> : <span className="num">{b}</span>;
      },
      sortValue: (r) => num(r.active_package_balance),
    },
  ];

  const plural = (n: number) => (n === 1 ? "" : "s");

  const smsCount = smsTarget === "segment" ? (segView?.ids.size ?? 0) : selected.size;
  const smsTitle =
    smsTarget === "segment" && segView
      ? `Text segment: ${segView.segment.name}`
      : `Text ${selected.size} client${plural(selected.size)}`;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <div className="page-sub">
            {total} client{plural(total)}
            {filtered.length !== total ? ` · ${filtered.length} shown` : ""}
          </div>
        </div>
        <div className="page-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {qError && <div className="error-banner">Failed to load clients: {qError}</div>}
      {actionError && !tagModalOpen && !smsModalOpen && (
        <div className="error-banner">{actionError}</div>
      )}

      {segmentId && (
        <div className="info-banner flex" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          {segViewError ? (
            <span>Failed to load segment: {segViewError}</span>
          ) : !segView ? (
            <span>
              <span className="spinner" /> Loading segment members…
            </span>
          ) : (
            <span>
              Segment <span className="bold">{segView.segment.name}</span> ·{" "}
              {segView.ids.size} member{plural(segView.ids.size)}
            </span>
          )}
          {segView && segView.ids.size > 0 && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => openSmsModal("segment")}
              disabled={busy}
            >
              Text all {segView.ids.size}
            </button>
          )}
          <span className="spacer" />
          <button
            type="button"
            className="btn-link"
            title="Clear segment view"
            aria-label="Clear segment view"
            onClick={() => navigate("/clients")}
          >
            ✕
          </button>
        </div>
      )}

      <div className="toolbar">
        <input
          className="search-input"
          placeholder="Search name, email, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search clients"
        />
        <select
          value={pkgFilter}
          onChange={(e) => setPkgFilter(e.target.value as PkgFilter)}
          aria-label="Filter by package"
        >
          <option value="any">Package: any</option>
          <option value="yes">Has active package</option>
          <option value="no">No active package</option>
        </select>
        <label
          className="checkbox nowrap"
          title="Last visit at least this many weeks ago; excludes opted-out and snoozed clients"
        >
          <input
            type="checkbox"
            checked={dueOnly}
            onChange={(e) => setDueOnly(e.target.checked)}
          />
          Due ≥
          <input
            type="number"
            min={1}
            value={dueWeeks}
            style={{ width: 56 }}
            onChange={(e) => setDueWeeks(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
            aria-label="Rebook threshold in weeks"
          />
          w
        </label>
        <input
          type="number"
          placeholder="LTV min"
          value={ltvMin}
          style={{ width: 84 }}
          onChange={(e) => setLtvMin(e.target.value)}
          aria-label="Minimum LTV"
        />
        <input
          type="number"
          placeholder="LTV max"
          value={ltvMax}
          style={{ width: 84 }}
          onChange={(e) => setLtvMax(e.target.value)}
          aria-label="Maximum LTV"
        />
        <select
          value={recency}
          onChange={(e) => setRecency(e.target.value)}
          aria-label="Filter by last-visit recency"
        >
          <option value="">Last visit: any</option>
          <option value="2">Within 2 weeks</option>
          <option value="4">Within 4 weeks</option>
          <option value="8">Within 8 weeks</option>
          <option value="12">Within 12 weeks</option>
          <option value="26">Within 26 weeks</option>
        </select>
        <select
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          aria-label="Filter by tag"
        >
          <option value="">Tag: any</option>
          {allTags.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          aria-label="Filter by acquisition source"
        >
          <option value="">Source: any</option>
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          <option value="none">No source</option>
        </select>
        <select
          value={segFilter}
          onChange={(e) => setSegFilter(e.target.value)}
          aria-label="Filter by RFM segment"
        >
          <option value="">Segment: any</option>
          {Object.entries(SEGMENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={colorFilter}
          onChange={(e) => setColorFilter(e.target.value)}
          aria-label="Filter by retention color"
        >
          <option value="">Color: any</option>
          {RETENTION_COLORS.map((c) => (
            <option key={c} value={c}>
              {RETENTION_EMOJI[c]} {RETENTION_LABELS[c]}
            </option>
          ))}
        </select>
        <label
          className="checkbox nowrap"
          title="Whales: avg ticket ≥ $600 or LTV ≥ $2,000"
        >
          <input
            type="checkbox"
            checked={whaleOnly}
            onChange={(e) => setWhaleOnly(e.target.checked)}
          />
          Whales
        </label>
        {hasActiveFilters && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters}>
            Reset
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <div className="toolbar">
          <span className="bold">
            {selected.size} selected
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setActionError(null);
              setTagModalOpen(true);
            }}
            disabled={busy}
          >
            Add tag
          </button>
          <select
            value={bulkSource}
            onChange={(e) => setBulkSource(e.target.value)}
            aria-label="Set acquisition source for selected"
          >
            <option value="">Set source…</option>
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            <option value="clear">Clear source</option>
          </select>
          {bulkSource !== "" && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={bulkSetSource}
              disabled={busy}
            >
              Apply
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => openSmsModal("selected")}
            disabled={busy}
          >
            Text selected
          </button>
          <span className="spacer" />
          <button type="button" className="btn-link" onClick={selectAllFiltered}>
            Select all {filtered.length} shown
          </button>
          <button type="button" className="btn-link" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <span className="spinner" /> Loading clients…
        </div>
      ) : (
        <>
          <div className="hide-mobile">
            <DataTable<ClientRow>
              columns={columns}
              rows={filtered}
              defaultSortKey="full_name"
              onRowClick={(r) => navigate(`/clients/${r.id}`)}
              rowClassName={(r) => (selected.has(String(r.id)) ? "selected" : undefined)}
              emptyMessage="No clients match the current filters"
            />
          </div>

          <div className="show-mobile client-cards">
            {filtered.map((r) => (
              <div
                key={String(r.id)}
                className="client-card"
                onClick={() => navigate(`/clients/${r.id}`)}
              >
                <div className="client-card-head">
                  <span className="flex">
                    <input
                      type="checkbox"
                      checked={selected.has(String(r.id))}
                      onChange={() => toggleSelect(r.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${r.full_name}`}
                    />
                    <span className="client-card-name">
                      <RetentionDot color={retentionColor(r)} /> {r.full_name}
                    </span>
                  </span>
                  <span className="nowrap">
                    <SegmentChip segment={r.rfm_segment} link />
                    <WhaleBadge whale={r.is_whale} />
                    <PackageBadge balance={num(r.active_package_balance)} />
                    <DueBadge weeks={num(r.weeks_since_last_visit)} threshold={dueWeeks} />
                  </span>
                </div>
                <div className="client-card-meta">
                  {r.phone_e164 && (
                    <a href={telHref(r.phone_e164)} onClick={(e) => e.stopPropagation()}>
                      {phoneDisplay(r.phone_e164)}
                    </a>
                  )}
                  <span>{money(num(r.ltv) ?? 0)}</span>
                  <span>
                    {num(r.visits) ?? 0} visit{plural(num(r.visits) ?? 0)}
                  </span>
                  <span>
                    {r.last_visit ? `${num(r.weeks_since_last_visit)}w ago` : "never visited"}
                  </span>
                  {r.phone_e164 && !r.sms_opt_out && (
                    <button
                      type="button"
                      className="btn-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${r.id}?compose=1`);
                      }}
                    >
                      💬 Text
                    </button>
                  )}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="empty-state">No clients match the current filters</div>
            )}
          </div>

          <div className="table-footer">
            <span>
              Showing {filtered.length} of {total} client{plural(total)}
              {selected.size > 0 ? ` · ${selected.size} selected` : ""}
            </span>
            <span className="flex">
              {selected.size === 0 && filtered.length > 0 && (
                <button type="button" className="btn-link" onClick={selectAllFiltered}>
                  Select all shown
                </button>
              )}
              <button
                type="button"
                className="btn-link"
                onClick={exportCsv}
                disabled={filtered.length === 0}
              >
                Export CSV
              </button>
            </span>
          </div>
        </>
      )}

      <Modal
        open={tagModalOpen}
        title={`Add tag to ${selected.size} client${plural(selected.size)}`}
        onClose={() => setTagModalOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setTagModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={bulkAddTag}
              disabled={busy || !tagDraft.trim()}
            >
              {busy ? "Adding…" : "Add tag"}
            </button>
          </>
        }
      >
        {actionError && <div className="error-banner">{actionError}</div>}
        <div className="field">
          <label htmlFor="bulk-tag-input">Tag</label>
          <input
            id="bulk-tag-input"
            autoFocus
            value={tagDraft}
            placeholder="e.g. vip"
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                bulkAddTag();
              }
            }}
          />
          <div className="field-hint">Tags are lower-cased; existing tags are left as-is.</div>
        </div>
      </Modal>

      <Modal
        open={smsModalOpen}
        title={smsTitle}
        onClose={() => setSmsModalOpen(false)}
        footer={
          bulkResult ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setSmsModalOpen(false)}
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setSmsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={bulkSendText}
                disabled={busy || !smsBody.trim()}
              >
                {busy
                  ? smsProgress
                    ? `Sending ${smsProgress.sent}/${smsProgress.total}…`
                    : "Sending…"
                  : `Send to ${smsCount}`}
              </button>
            </>
          )
        }
      >
        {actionError && <div className="error-banner">{actionError}</div>}
        {bulkResult ? (
          <>
            <div className="success-banner">
              Sent {bulkResult.sent} message{plural(bulkResult.sent)}.
            </div>
            {bulkResult.skipped.length > 0 && (
              <div className="info-banner">
                Skipped {bulkResult.skipped.length}:
                <ul className="mt-1" style={{ margin: 0, paddingLeft: 18 }}>
                  {bulkResult.skipped.map((s) => (
                    <li key={String(s.client_id)}>
                      Client {String(s.client_id)} — {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <>
            {busy && smsProgress && (
              <div className="info-banner">
                <span className="spinner" /> Sending {smsProgress.sent}/{smsProgress.total}… keep
                this window open until it finishes.
              </div>
            )}
            <div className="field">
              <label htmlFor="bulk-sms-body">Message</label>
              <textarea
                id="bulk-sms-body"
                rows={4}
                value={smsBody}
                placeholder={"Hi {first_name}, it's Fresh IV Drips…"}
                onChange={(e) => setSmsBody(e.target.value)}
                disabled={busy}
              />
              <div className="field-hint">
                {"{first_name}"} is replaced per recipient. Opted-out clients and clients without a
                phone number are skipped automatically. Messages go out in batches of 40, paced
                ~1.3s apart (Project Blue rate limit) — large selections take a few minutes.
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
